import axios from 'axios';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEyeSlash,
  faBuilding,
  faEnvelope,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { customerAuthService } from '@/services/customer-auth.service';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { CUSTOMER_PORTAL_ROUTES } from '@/routes/customer-portal-routes';
import { CustomerFormField } from '@/components/customer-portal/CustomerFormField';
import { CustomerInput } from '@/components/customer-portal/CustomerInput';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';

function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const message = (err.response?.data as { error?: { message?: string } } | undefined)?.error
      ?.message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return fallback;
}

export function CustomerLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // User Profile P2 — the change-password page signs the contact out on purpose
  // (every session ended) and lands here with a notice explaining why.
  const notice =
    (location.state as { notice?: string } | null)?.notice === 'password-changed'
      ? 'Your password was changed. Please sign in with your new password.'
      : null;
  const setAuth = useCustomerAuthStore((s) => s.setAuth);
  const isAuthenticated = useCustomerAuthStore((s) => s.isAuthenticated);

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  // An already-authenticated customer landing on /login is sent straight to the dashboard.
  if (isAuthenticated) {
    return <Navigate to={CUSTOMER_PORTAL_ROUTES.DASHBOARD} replace />;
  }

  const validate = () => {
    const e: Record<string, string> = {};
    if (!identifier.trim()) e.identifier = 'Enter your email, username, or mobile number';
    if (!password) e.password = 'Enter your password';
    return e;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setFormError(null);
    setLoading(true);
    try {
      const res = await customerAuthService.login(identifier.trim(), password);
      setAuth(res.customer, res.accessToken, res.refreshToken);
      const redirect = searchParams.get('redirect');
      navigate(redirect ? decodeURIComponent(redirect) : CUSTOMER_PORTAL_ROUTES.DASHBOARD, {
        replace: true,
      });
    } catch (err) {
      if (axios.isAxiosError(err) && !err.response) {
        setFormError('Unable to reach the server. Check your connection and try again.');
      } else if (axios.isAxiosError(err) && err.response?.status === 401) {
        setFormError(extractErrorMessage(err, 'Invalid credentials.'));
      } else if (axios.isAxiosError(err) && err.response?.status === 403) {
        setFormError(
          extractErrorMessage(err, 'Too many failed attempts — please try again later.'),
        );
      } else {
        setFormError(extractErrorMessage(err, 'Something went wrong. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-customer-bg px-4 py-8 sm:px-6">
      <div className="customer-fade-in w-full max-w-[420px] lg:max-w-5xl">
        <div className="grid overflow-hidden rounded-customer-lg border border-customer-border bg-customer-surface shadow-customer-lg lg:grid-cols-2">
          {/* Brand panel — decorative only, and desktop only. Every colour composes
              an existing --customer-* token (D-1), so it tracks all six skins and
              all three modes. Hidden below `lg` so mobile pays no DOM or paint cost. */}
          <div className="customer-login-brand relative hidden flex-col justify-between border-r border-customer-border p-10 lg:flex">
            <div
              className="customer-login-grid pointer-events-none absolute inset-0"
              aria-hidden="true"
            />
            <div className="relative">
              <p className="text-2xl font-semibold leading-snug tracking-[-0.02em] text-customer-text">
                Your equipment, jobs and approvals — in one place.
              </p>
              {/* `--customer-text`, not `-secondary`: the panel's tinted ground eats the
                  secondary token's AA headroom in 11 of 12 skin/mode combinations
                  (measured). Hierarchy here comes from size and weight, not colour. */}
              <p className="mt-4 max-w-[36ch] text-sm font-normal text-customer-text">
                Review inspection reports, track job progress and respond to quotations across your
                sites.
              </p>
            </div>
            <p className="relative pt-10 text-xs font-medium uppercase tracking-widest text-customer-text">
              Equipment · Jobs · Approvals
            </p>
          </div>

          {/* Form panel — token-driven, unchanged behaviour. */}
          <div className="flex flex-col justify-center p-8 sm:p-10">
            <div className="mb-7 flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-customer bg-customer-primary">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="h-5 w-5 text-customer-action-ink"
                  aria-hidden="true"
                />
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight text-customer-text">
                  Service Portal
                </p>
                <p className="text-xs font-medium uppercase tracking-widest text-customer-primary-text">
                  Customer Portal
                </p>
              </div>
            </div>

            <h1 className="text-[clamp(1.3125rem,2.1vw,1.6875rem)] font-semibold tracking-[-0.02em] text-customer-text">
              Sign in
            </h1>
            <p className="mt-2 text-sm text-customer-text-secondary">
              Access your equipment, jobs, and approvals.
            </p>

            {formError && (
              <div
                role="alert"
                className="customer-modal-in mt-6 rounded-customer-sm border border-customer-warning/40 bg-customer-warning/10 px-3 py-2 text-sm text-customer-text"
              >
                {formError}
              </div>
            )}
            {notice && !formError && (
              <div
                role="status"
                className="customer-modal-in mt-6 rounded-customer-sm border border-customer-primary/40 bg-customer-primary/10 px-3 py-2 text-sm text-customer-text"
              >
                {notice}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-5">
              <CustomerFormField
                label="Email, username, or mobile"
                htmlFor="customer-identifier"
                required
                error={errors.identifier ?? null}
              >
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-customer-text-secondary"
                    aria-hidden="true"
                  />
                  <CustomerInput
                    id="customer-identifier"
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="customer@company.com"
                    autoComplete="username"
                    autoFocus
                    disabled={loading}
                    invalid={!!errors.identifier}
                    aria-describedby={errors.identifier ? 'customer-identifier-error' : undefined}
                    className="pl-10"
                  />
                </div>
              </CustomerFormField>

              <CustomerFormField
                label="Password"
                htmlFor="customer-password"
                required
                error={errors.password ?? null}
              >
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faLock}
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-customer-text-secondary"
                    aria-hidden="true"
                  />
                  <CustomerInput
                    id="customer-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    disabled={loading}
                    invalid={!!errors.password}
                    aria-describedby={errors.password ? 'customer-password-error' : undefined}
                    className="pl-10 pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-customer-text-secondary hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-inset"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="h-4 w-4" />
                  </button>
                </div>
              </CustomerFormField>

              <CustomerButton
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                loadingLabel="Signing in..."
                className="mt-1"
              >
                Sign in
              </CustomerButton>

              {/* D-139 — the forgot-password entry point. It is NAVIGATION ONLY: it
                  carries no credential, performs no lookup and reveals nothing about
                  whether an account exists. `?mode=reset` selects the recovery wording
                  on the existing activation page; the OTP flow behind it is unchanged.
                  Placed after the submit control so tab order stays
                  identifier → password → submit (D-139 rule 7). */}
              <p className="text-center">
                <Link
                  to={`${CUSTOMER_PORTAL_ROUTES.ACTIVATE}?mode=reset`}
                  className="rounded-customer-sm text-xs font-medium text-customer-primary-text underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
                >
                  Forgot password?
                </Link>
              </p>
            </form>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-customer-text-secondary">
          This is a restricted area for authorized customers only.
        </p>
      </div>
    </div>
  );
}
